import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getBannerAdsPublic } from '../api/apiUtils';

/**
 * Auto-rotating promotional banner strip for the storefront. Pulls active
 * banners from the API (public, so it works for logged-out visitors) and
 * hides itself entirely when there are none — never a broken empty box.
 */
const BannerCarousel = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getBannerAdsPublic();
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
        const active = list
          .filter((b) => (b?.status ? b.status === 'active' : true) && (b?.imageUrl))
          .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
        if (!cancelled) setBanners(active);
      } catch {
        /* silent — banner strip is best-effort */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const count = banners.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % Math.max(count, 1)), [count]);
  const prev = () => setIndex((i) => (i - 1 + count) % Math.max(count, 1));

  useEffect(() => {
    if (count <= 1) return undefined;
    const t = setInterval(next, 4500);
    return () => clearInterval(t);
  }, [count, next]);

  const current = useMemo(() => banners[index], [banners, index]);

  const openBanner = (banner) => {
    const url = banner?.bannerUrl;
    if (!url) return;
    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = url;
    }
  };

  if (loading) {
    return (
      <section className="bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full h-40 sm:h-56 rounded-2xl bg-gray-200 animate-pulse" />
        </div>
      </section>
    );
  }

  if (count === 0) return null;

  return (
    <section className="bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative w-full overflow-hidden rounded-2xl shadow-sm group">
          <div className="relative h-40 sm:h-56 md:h-64">
            <AnimatePresence initial={false} mode="wait">
              <motion.button
                key={current?.id ?? index}
                type="button"
                onClick={() => openBanner(current)}
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 w-full h-full text-left"
              >
                <img
                  src={current?.imageUrl}
                  alt={current?.title || current?.name || 'Offer'}
                  className="w-full h-full object-cover"
                />
                {(current?.title || current?.description) && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent flex flex-col justify-end p-5 sm:p-6">
                    {current?.title && (
                      <h3 className="text-white text-lg sm:text-2xl font-bold drop-shadow">
                        {current.title}
                      </h3>
                    )}
                    {current?.description && (
                      <p className="text-white/90 text-xs sm:text-sm mt-1 line-clamp-2 max-w-xl">
                        {current.description}
                      </p>
                    )}
                  </div>
                )}
              </motion.button>
            </AnimatePresence>
          </div>

          {count > 1 && (
            <>
              <button
                type="button"
                onClick={prev}
                aria-label="Previous"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ChevronLeft className="w-5 h-5 text-gray-800" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ChevronRight className="w-5 h-5 text-gray-800" />
              </button>

              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {banners.map((b, i) => (
                  <button
                    key={b?.id ?? i}
                    type="button"
                    aria-label={`Go to banner ${i + 1}`}
                    onClick={() => setIndex(i)}
                    className={`h-1.5 rounded-full transition-all ${
                      i === index ? 'w-6 bg-white' : 'w-1.5 bg-white/60'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default BannerCarousel;
